/**
 * Manager Access Service
 * 
 * Handles all manager access request operations including:
 * - Creating requests
 * - Approving/denying requests
 * - Revoking access
 * - Sending notifications
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  getDocs,
  query, 
  where, 
  serverTimestamp,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  ManagerAccessRequest, 
  ManagerAccessResponse, 
  CompanyManagerRelation,
  ManagerAccessNotification
} from '../types/managerAccess.types';

/**
 * Create a new manager access request
 */
export const createManagerAccessRequest = async (
  requesterId: string,
  requesterName: string,
  requesterEmail: string,
  requesterPhone: string,
  companyName: string,
  companyKvK: string,
  companyEmail: string,
  companyPhone: string,
  message?: string
): Promise<string> => {
  try {
    console.log('📝 Creating manager access request:', {
      requesterId,
      companyName,
      companyKvK
    });

    // Try to find company owner by KVK number
    const companiesRef = collection(db, 'userProfiles');
    const kvkQuery = query(
      companiesRef, 
      where('company', '==', companyName),
      where('networkclubKvk', '==', companyKvK)
    );
    
    const kvkSnapshot = await getDocs(kvkQuery);
    let companyOwnerId: string | undefined;

    if (!kvkSnapshot.empty) {
      companyOwnerId = kvkSnapshot.docs[0].id;
      console.log('✅ Found company owner:', companyOwnerId);
    } else {
      console.log('⚠️ Company owner not found by KVK, will need manual matching');
    }

    // Create the request
    const requestData: Omit<ManagerAccessRequest, 'id'> = {
      requesterId,
      requesterName,
      requesterEmail,
      requesterPhone,
      companyName,
      companyKvK,
      companyOwnerId,
      status: 'pending',
      requestedAt: Timestamp.now() as any,
      message
    };

    const requestsRef = collection(db, 'managerAccessRequests');
    const requestDoc = await addDoc(requestsRef, requestData);

    console.log('✅ Manager access request created:', requestDoc.id);

    // If we found the company owner, send them a notification
    if (companyOwnerId) {
      await createManagerNotification(
        companyOwnerId,
        requesterId,
        requesterName,
        requestDoc.id,
        companyName,
        'manager_request'
      );
    }

    return requestDoc.id;
  } catch (error) {
    console.error('❌ Error creating manager access request:', error);
    throw error;
  }
};

/**
 * Approve a manager access request
 */
export const approveManagerAccessRequest = async (
  requestId: string,
  reviewerId: string
): Promise<void> => {
  try {
    console.log('✅ Approving manager access request:', requestId);

    const requestRef = doc(db, 'managerAccessRequests', requestId);
    const requestSnap = await getDoc(requestRef);

    if (!requestSnap.exists()) {
      throw new Error('Request not found');
    }

    const requestData = requestSnap.data() as ManagerAccessRequest;

    // Update request status
    await updateDoc(requestRef, {
      status: 'approved',
      reviewedAt: serverTimestamp(),
      reviewedBy: reviewerId
    });

    // Create manager-company relation
    const relationData: Omit<CompanyManagerRelation, 'id'> = {
      companyOwnerId: reviewerId,
      managerId: requestData.requesterId,
      managerName: requestData.requesterName,
      managerEmail: requestData.requesterEmail,
      managerPhone: requestData.requesterPhone,
      grantedAt: Timestamp.now() as any,
      grantedBy: reviewerId,
      isActive: true,
      permissions: {
        canPost: true,
        canManageContent: false,
        canInviteMembers: false,
        canViewAnalytics: false
      }
    };

    const relationsRef = collection(db, 'companyManagerRelations');
    await addDoc(relationsRef, relationData);

    // Update user profile to mark as manager
    const userRef = doc(db, 'userProfiles', requestData.requesterId);
    await updateDoc(userRef, {
      isManager: true,
      managedCompanyId: reviewerId,
      managedCompanyName: requestData.companyName
    });

    // Send approval notification to requester
    await createManagerNotification(
      requestData.requesterId,
      reviewerId,
      'Company Owner',
      requestId,
      requestData.companyName,
      'manager_approved'
    );

    console.log('✅ Manager access approved successfully');
  } catch (error) {
    console.error('❌ Error approving manager access:', error);
    throw error;
  }
};

/**
 * Deny a manager access request
 */
export const denyManagerAccessRequest = async (
  requestId: string,
  reviewerId: string,
  reason: string
): Promise<void> => {
  try {
    console.log('❌ Denying manager access request:', requestId);

    const requestRef = doc(db, 'managerAccessRequests', requestId);
    const requestSnap = await getDoc(requestRef);

    if (!requestSnap.exists()) {
      throw new Error('Request not found');
    }

    const requestData = requestSnap.data() as ManagerAccessRequest;

    // Update request status
    await updateDoc(requestRef, {
      status: 'denied',
      reviewedAt: serverTimestamp(),
      reviewedBy: reviewerId,
      denialReason: reason
    });

    // Send denial notification to requester
    await createManagerNotification(
      requestData.requesterId,
      reviewerId,
      'Company Owner',
      requestId,
      requestData.companyName,
      'manager_denied'
    );

    console.log('✅ Manager access denied');
  } catch (error) {
    console.error('❌ Error denying manager access:', error);
    throw error;
  }
};

/**
 * Revoke manager access
 */
export const revokeManagerAccess = async (
  relationId: string,
  revokedBy: string,
  reason: string
): Promise<void> => {
  try {
    console.log('🚫 Revoking manager access:', relationId);

    const relationRef = doc(db, 'companyManagerRelations', relationId);
    const relationSnap = await getDoc(relationRef);

    if (!relationSnap.exists()) {
      throw new Error('Relation not found');
    }

    const relationData = relationSnap.data() as CompanyManagerRelation;

    // Update relation status
    await updateDoc(relationRef, {
      isActive: false,
      revokedAt: serverTimestamp(),
      revokedBy,
      revocationReason: reason
    });

    // Update user profile
    const userRef = doc(db, 'userProfiles', relationData.managerId);
    await updateDoc(userRef, {
      isManager: false,
      managedCompanyId: null,
      managedCompanyName: null
    });

    // Send revocation notification
    await createManagerNotification(
      relationData.managerId,
      revokedBy,
      'Company Owner',
      relationId,
      'Your managed company',
      'manager_revoked'
    );

    console.log('✅ Manager access revoked');
  } catch (error) {
    console.error('❌ Error revoking manager access:', error);
    throw error;
  }
};

/**
 * Get pending manager requests for a company owner
 */
export const getPendingManagerRequests = async (
  companyOwnerId: string
): Promise<ManagerAccessRequest[]> => {
  try {
    const requestsRef = collection(db, 'managerAccessRequests');
    const q = query(
      requestsRef,
      where('companyOwnerId', '==', companyOwnerId),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ManagerAccessRequest[];
  } catch (error) {
    console.error('❌ Error getting pending requests:', error);
    return [];
  }
};

/**
 * Get active managers for a company
 */
export const getActiveManagers = async (
  companyOwnerId: string
): Promise<CompanyManagerRelation[]> => {
  try {
    const relationsRef = collection(db, 'companyManagerRelations');
    const q = query(
      relationsRef,
      where('companyOwnerId', '==', companyOwnerId),
      where('isActive', '==', true)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CompanyManagerRelation[];
  } catch (error) {
    console.error('❌ Error getting active managers:', error);
    return [];
  }
};

/**
 * Create a notification for manager access events
 */
const createManagerNotification = async (
  userId: string,
  fromUserId: string,
  fromUserName: string,
  requestId: string,
  companyName: string,
  type: ManagerAccessNotification['type']
): Promise<void> => {
  try {
    const messages = {
      manager_request: `${fromUserName} heeft toegang aangevraagd als manager voor ${companyName}`,
      manager_approved: `Je aanvraag voor manager toegang bij ${companyName} is goedgekeurd`,
      manager_denied: `Je aanvraag voor manager toegang bij ${companyName} is afgewezen`,
      manager_revoked: `Je manager toegang bij ${companyName} is ingetrokken`
    };

    const notificationData: Omit<ManagerAccessNotification, 'id'> = {
      type,
      userId,
      fromUserId,
      fromUserName,
      requestId,
      companyName,
      message: messages[type],
      read: false,
      createdAt: Timestamp.now() as any
    };

    const notificationsRef = collection(db, 'notifications');
    await addDoc(notificationsRef, notificationData);

    console.log('✅ Manager notification created:', type);
  } catch (error) {
    console.error('❌ Error creating notification:', error);
  }
};
